import React, { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, FileText, Calendar, Building, Clock, MessageCircle, Filter, CheckCircle, XCircle, AlertCircle, Eye, Truck, Package, Edit2, Trash2, ChevronDown, ChevronUp, Warehouse, Loader2, User } from "lucide-react";
import PurchaseOrderModal from "../Modal/PurchaseOrderModal";
import PurchaseOrderEditModal from "./PurchaseOrderEditModal";
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import ActivityFeedModal_PO from "../Activity/ActivityFeedModal_PO";

const STATUS_MAP = {
  0: { label: "Draft", color: "gray", icon: AlertCircle },
  1: { label: "Pending", color: "yellow", icon: Clock },
  2: { label: "Approved", color: "green", icon: CheckCircle },
  3: { label: "Rejected", color: "red", icon: XCircle },
};

const RECEIVING_STATUS_MAP = {
  "Pending": { color: "yellow", icon: Clock },
  "Partial": { color: "blue", icon: Package },
  "Complete": { color: "green", icon: CheckCircle },
  "Cancelled": { color: "red", icon: XCircle },
};

export default function PurchaseOrderWithGR({ setCurrentPage }) {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [companies, setCompanies] = useState({});
  const [vendors, setVendors] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [receivingFilter, setReceivingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("dateNewest");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const [showFeedModal, setShowFeedModal] = useState(false);
  const [feedOrder, setFeedOrder] = useState(null);
  // 🔥 GR EXPANSION STATES
  const [expandedPO, setExpandedPO] = useState(null);
  const [grData, setGrData] = useState({});
  const [loadingGR, setLoadingGR] = useState({});

  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [poApprovals, setPoApprovals] = useState({});
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState(null);

  // 🔥 ADD THIS NEW FUNCTION - Fetch PO Approval Remarks

  const fetchPOApprovals = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Approval`);
      const data = await res.json();

      // ✅ Group by poHeaderId - FORCE NUMBER KEYS!
      const approvalMap = {};
      data.forEach(approval => {
        const poId = parseInt(approval.poHeaderId); // ✅ CONVERT TO NUMBER!

        if (!approvalMap[poId]) {
          approvalMap[poId] = [];
        }
        approvalMap[poId].push(approval);
      });

      console.log("📊 Grouped Approvals:", approvalMap);
      setPoApprovals(approvalMap);
    } catch (error) {
      console.error("Error fetching PO approvals:", error);
    }
  };

  // ========== API FUNCTIONS ==========
  //
  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Company`);
      if (!res.ok) throw new Error("Failed to fetch companies");
      const data = await res.json();
      const companyMap = {};
      data.forEach((c) => {
        companyMap[c.id] = c.companyName || c.CompanyName || "Unknown";
      });
      setCompanies(companyMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      if (!res.ok) throw new Error("Failed to fetch vendors");
      const data = await res.json();
      const vendorMap = {};
      data.forEach((v) => {
        vendorMap[v.id] = v.vendorName || v.VendorName || "Unknown";
      });
      setVendors(vendorMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);
      if (!res.ok) throw new Error("Failed to fetch purchase orders");
      const data = await res.json();

      console.log("📦 Fetched PO data:", data.length, "orders");

      // Get current user from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        console.error("❌ No user found in localStorage");
        setOrders([]);
        setFiltered([]);
        return;
      }

      console.log("👤 Current User:", {
        userId: user.userId,
        username: user.username,
        positionId: user.positionId,
        firstName: user.firstName
      });

      // Fetch user levels
      const levelRes = await fetch(`${API_BASE_URL}api/UserLevels`);
      const levels = await levelRes.json();

      console.log("📋 Available User Levels:", levels);

      // ✅ CRITICAL FIX: Match positionId (string) with userLevelName (string)
      // Use case-insensitive comparison to handle any casing issues
      const userLevel = levels.find(
        l => l.isActive === 1 &&
          user.positionId?.toUpperCase().includes(l.userLevelName?.toUpperCase())
      );

      if (!userLevel) {
        console.error("❌ No matching user level found!");
        console.error("User positionId:", user.positionId);
        console.error("Available levels:", levels.map(l => ({
          id: l.id,
          name: l.userLevelName,
          isActive: l.isActive
        })));

        Swal.fire({
          icon: 'warning',
          title: 'Access Configuration Issue',
          html: `
            <p>Your position <strong>"${user.positionId}"</strong> is not configured in User Levels.</p>
            <p>Please contact your system administrator.</p>
            <hr/>
            <small>Available levels: ${levels.map(l => l.userLevelName).join(', ')}</small>
          `,
          confirmButtonColor: '#dc2626'
        });

        setOrders([]);
        setFiltered([]);
        return;
      }

      console.log("✅ Matched User Level:", {
        id: userLevel.id,
        name: userLevel.userLevelName,
        accessViewAllData: userLevel.accessViewAllData,
        accessVIewUserOnly: userLevel.accessVIewUserOnly
      });

      let filteredData = [];

      // ✅ ACCESS LOGIC PRIORITY:
      // 1. If accessViewAllData is TRUE → Show ALL orders
      // 2. Else if accessVIewUserOnly is TRUE → Show only user's orders (where preparedBy matches userId)
      // 3. Else → Show nothing (both are null/false)

      if (userLevel.accessViewAllData === true) {
        console.log("🌍 Access: VIEW ALL DATA - Showing all", data.length, "orders");
        filteredData = data;
      }
      else if (userLevel.accessVIewUserOnly === true) {
        console.log("👤 Access: VIEW USER ONLY - Filtering by preparedBy === userId");
        filteredData = data.filter(order => {
          const match = order.preparedBy === user.userId;
          if (!match) {
            console.log(`   ⏭️  Skipping PO ${order.poNumber} (preparedBy: ${order.preparedBy})`);
          }
          return match;
        });
        console.log(`   ✅ Found ${filteredData.length} orders prepared by user ${user.userId}`);
      }
      else {
        console.warn("⚠️ No access permissions granted - showing no data");
        console.warn("Both accessViewAllData and accessVIewUserOnly are null/false");
        filteredData = [];
      }

      console.log("📊 Final filtered data:", filteredData.length, "orders");
      setOrders(filteredData);
      setFiltered(filteredData);


    } catch (error) {
      console.error("💥 Error in fetchOrders:", error);
      setOrders([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 FETCH GR FOR SPECIFIC PO
  const fetchGRForPO = async (poId) => {
    if (expandedPO === poId) { setExpandedPO(null); return; }
    if (grData[poId]) { setExpandedPO(poId); return; }

    setLoadingGR(prev => ({ ...prev, [poId]: true }));

    try {
      // ✅ Direct — GR_Header has POId field!
      const grHeaderRes = await fetch(`${API_BASE_URL}api/GR_Header`);
      const allGRHeaders = await grHeaderRes.json();
      const matchedHeaders = allGRHeaders.filter(h => h.poId === poId || h.pOId === poId);

      if (matchedHeaders.length === 0) {
        setGrData(prev => ({ ...prev, [poId]: [] }));
        setExpandedPO(poId);
        return;
      }

      // Fetch GR Details for each matched GR Header
      const grDetailsRes = await fetch(`${API_BASE_URL}api/GR_Details`);
      const allGRDetails = await grDetailsRes.json();

      const matchedHeaderIds = matchedHeaders.map(h => h.id);
      const matchedDetails = allGRDetails.filter(d => matchedHeaderIds.includes(d.grHeaderId));

      // Combine header info into each detail row
      const combined = matchedDetails.map(detail => {
        const header = matchedHeaders.find(h => h.id === detail.grHeaderId);
        return {
          ...detail,
          grNo: header?.grNo || header?.gRNo || 'N/A',
          grDate: header?.grDate || header?.gRDate || null,
          receivedBy: header?.receivedBy || 'Unknown',
          remarks: header?.remarks || '',
          status: header?.status,
        };
      });

      setGrData(prev => ({ ...prev, [poId]: combined }));
      setExpandedPO(poId);
    } catch (error) {
      console.error('Error fetching GR data:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load GR records', confirmButtonColor: '#dc2626' });
    } finally {
      setLoadingGR(prev => ({ ...prev, [poId]: false }));
    }
  };
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.positionId === 'SYSTEM ADMIN';
  const canEditDelete = (status) => isAdmin || status !== 2;

  const handleDeleteOrder = async (orderId, poNumber) => {
    const result = await Swal.fire({
      title: 'Delete Purchase Order?',
      html: `Are you sure you want to delete ${poNumber}? This will also delete all PO details!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/delete/${orderId}`, {
        method: "DELETE",
      });

      if (!detailsRes.ok) {
        console.warn("Failed to delete PO details or no details found");
      }

      const headerRes = await fetch(`${API_BASE_URL}api/T_PO_Header/${orderId}`, {
        method: "DELETE",
      });

      if (!headerRes.ok) throw new Error("Failed to delete purchase order");


      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "DELETE",
          module: "Purchase Order",
          description: `PO ${poNumber} was permanently deleted. PO ID: ${orderId}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${poNumber} has been deleted.`,
        timer: 2000,
        showConfirmButton: false
      });

      fetchOrders();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.message,
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setShowEditModal(true);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  useEffect(() => {
    fetchCompanies();
    fetchVendors();
    fetchOrders();
    fetchPOApprovals(); // ✅ ADD THIS!

  }, []);

  useEffect(() => {
    let result = [...orders];

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.poNumber?.toLowerCase().includes(term) ||
          o.shipTo_Location?.toLowerCase().includes(term) ||
          o.shipTo_ContactPerson?.toLowerCase().includes(term) ||
          companies[o.companyId]?.toLowerCase().includes(term) ||
          vendors[o.vendorId]?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((o) => o.status === parseInt(statusFilter));
    }

    if (receivingFilter !== "all") {
      result = result.filter((o) => o.receivingStatus === receivingFilter);
    }

    switch (sortBy) {
      case "dateNewest":
        result.sort((a, b) => new Date(b.poCreatedDate) - new Date(a.poCreatedDate));
        break;
      case "dateOldest":
        result.sort((a, b) => new Date(a.poCreatedDate) - new Date(b.poCreatedDate));
        break;
      case "poNoAsc":
        result.sort((a, b) => a.poNumber.localeCompare(b.poNumber));
        break;
      case "poNoDesc":
        result.sort((a, b) => b.poNumber.localeCompare(a.poNumber));
        break;
      case "statusAsc":
        result.sort((a, b) => a.status - b.status);
        break;
      default:
        break;
    }

    setFiltered(result);
    setPage(1);
  }, [search, statusFilter, receivingFilter, sortBy, orders, companies, vendors]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const handleStatusClick = (orderId, status) => {
    // ✅ CONVERT TO NUMBER!
    const poId = parseInt(orderId);

    console.log("🔍 Looking for PO ID:", poId);
    console.log("📦 Available approvals:", poApprovals);

    const approvals = poApprovals[poId] || [];

    console.log("✅ Found approvals:", approvals);

    if (approvals.length === 0) {
      setSelectedRemarks({
        status: status,
        approvals: [],
        message: "No approval history available for this PO."
      });
    } else {
      const sortedApprovals = approvals.sort(
        (a, b) => new Date(b.responseDateTime) - new Date(a.responseDateTime)
      );
      setSelectedRemarks({
        status: status,
        approvals: sortedApprovals
      });
    }

    setShowRemarksModal(true);
  };
  const getStatusBadge = (status, orderId) => {
    const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];
    const Icon = statusInfo.icon;
    const colorClasses = {
      gray: "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300",
      yellow: "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-900 border-yellow-300",
      green: "bg-gradient-to-r from-green-100 to-green-200 text-green-900 border-green-300",
      red: "bg-gradient-to-r from-red-100 to-red-200 text-red-900 border-red-300",
    };

    return (
      <button
        onClick={() => handleStatusClick(orderId, status)}
        className={`group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold shadow-lg border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 ${colorClasses[statusInfo.color]
          }`}
      >
        <Icon className="w-4 h-4" />
        <span>{statusInfo.label}</span>
        {/* 🔥 ANIMATED ARROW */}
        <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
      </button>
    );
  };

  const RemarksModal = () => {
    if (!showRemarksModal || !selectedRemarks) return null;

    const statusInfo = STATUS_MAP[selectedRemarks.status] || STATUS_MAP[0];
    const StatusIcon = statusInfo.icon;

    const getResponseLabel = (response) => {
      switch (response) {
        case 1: return { label: "Approved", color: "green", icon: CheckCircle };
        case 0: return { label: "Rejected", color: "red", icon: XCircle };
        case -1: return { label: "Pending Review", color: "yellow", icon: AlertCircle };
        default: return { label: "Unknown", color: "gray", icon: AlertCircle };
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
        onClick={() => setShowRemarksModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className={`bg-gradient-to-r ${statusInfo.color === 'green' ? 'from-green-500 to-green-600' :
            statusInfo.color === 'red' ? 'from-red-500 to-red-600' :
              statusInfo.color === 'yellow' ? 'from-yellow-500 to-yellow-600' :
                'from-gray-500 to-gray-600'
            } p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <StatusIcon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Approval History</h2>
                  <p className="text-white/90 text-sm">Status: {statusInfo.label}</p>
                </div>
              </div>
              <button
                onClick={() => setShowRemarksModal(false)}
                className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 group"
              >
                <XCircle className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
          </div>

          {/* CONTENT */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {selectedRemarks.approvals.length === 0 ? (
              <div className="text-center py-12">
                <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">{selectedRemarks.message}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedRemarks.approvals.map((approval, index) => {
                  const responseInfo = getResponseLabel(approval.response);
                  const ResponseIcon = responseInfo.icon;

                  return (
                    <div
                      key={index}
                      className="group bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all duration-300 animate-fadeIn"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {/* HEADER */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-${responseInfo.color}-100`}>
                            <ResponseIcon className={`w-5 h-5 text-${responseInfo.color}-600`} />
                          </div>
                          <div>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold bg-${responseInfo.color}-100 text-${responseInfo.color}-700`}>
                              {responseInfo.label}
                            </span>
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(approval.responseDateTime).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-gray-500">Approver Level</p>
                          <p className="font-bold text-gray-700">{approval.approverUserLevel}</p>
                        </div>
                      </div>

                      {/* REMARKS */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4 group-hover:border-blue-300 transition-colors">
                        <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          REMARKS
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          {approval.responseRemarks || "No remarks provided."}
                        </p>
                      </div>

                      {/* APPROVER ID */}
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                        <User className="w-3 h-3" />
                        <span>Approver ID: <span className="font-mono font-semibold text-gray-700">#{approval.approverId}</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <button
              onClick={() => setShowRemarksModal(false)}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };
  const getReceivingBadge = (receivingStatus) => {
    if (!receivingStatus) return <span className="text-gray-400">—</span>;

    const statusInfo = RECEIVING_STATUS_MAP[receivingStatus] || RECEIVING_STATUS_MAP["Pending"];
    const Icon = statusInfo.icon;
    const colorClasses = {
      yellow: "bg-yellow-100 text-yellow-800",
      blue: "bg-blue-100 text-blue-800",
      green: "bg-green-100 text-green-800",
      red: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${colorClasses[statusInfo.color]
          }`}
      >
        <Icon className="w-3.5 h-3.5" />
        {receivingStatus}
      </span>
    );
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "—";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleMouseEnter = (e, text) => {
    if (!text || text.length <= 30) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      text: text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", x: 0, y: 0 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            <p className="text-gray-700 font-medium">Loading purchase orders...</p>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Package className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Purchase Orders with GR
          </h1>
        </div>
        <p className="text-gray-600 ml-13">View POs and their linked Goods Receipts</p>
        <div className="mt-4 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-sm border border-blue-100">
          <FileText className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-semibold text-gray-700">{filtered.length}</span>
          <span className="text-sm text-gray-500">Total Orders</span>
        </div>
      </div>

      {/* FILTERS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search PO, Company, Vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm"
          />
        </div>

        <div className="relative">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm appearance-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="0">Draft</option>
            <option value="1">Pending</option>
            <option value="2">Approved</option>
            <option value="3">Rejected</option>
          </select>
        </div>

        <div className="relative">
          <Truck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <select
            value={receivingFilter}
            onChange={(e) => setReceivingFilter(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm appearance-none cursor-pointer"
          >
            <option value="all">All Receiving Status</option>
            <option value="Pending">Pending</option>
            <option value="Partial">Partial</option>
            <option value="Complete">Complete</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className="relative lg:col-span-2">
          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm appearance-none cursor-pointer"
          >
            <option value="dateNewest">Date: Newest First</option>
            <option value="dateOldest">Date: Oldest First</option>
            <option value="poNoAsc">PO No: A-Z</option>
            <option value="poNoDesc">PO No: Z-A</option>
            <option value="statusAsc">Status: Low to High</option>
          </select>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">PO Number</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Company</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Vendor</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Ship To</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Date Created</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Delivery Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Receiving</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length > 0 ? (
                paginated.map((order, index) => (
                  <React.Fragment key={order.id}>
                    <tr
                      className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {/* 🔥 EXPAND/COLLAPSE BUTTON */}
                          <button
                            onClick={() => fetchGRForPO(order.id)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Show/Hide GR Records"
                          >
                            {loadingGR[order.id] ? (
                              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                            ) : expandedPO === order.id ? (
                              <ChevronUp className="w-4 h-4 text-blue-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          <div>
                            <p className="text-xs text-gray-500">#{order.id}</p>
                            <p className="font-semibold text-gray-900">{order.poNumber}</p>
                            <p className="text-xs text-blue-600">
                              {order.isVatInclusive ? "VAT Inc." : "VAT Ex."}
                              {order.isNonVAT && " • NON-VAT"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{companies[order.companyId] || "—"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {vendors[order.vendorId] || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          onMouseEnter={(e) =>
                            handleMouseEnter(e, `${order.shipTo_Location}\n${order.shipTo_Address1}\n${order.shipTo_Address2}`)
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          <p className="text-sm font-medium text-gray-900">
                            {truncateText(order.shipTo_Location, 20)}
                          </p>
                          <p className="text-xs text-gray-500">{order.shipTo_ContactPerson || "—"}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDate(order.poCreatedDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDate(order.deliveryDate)}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(order.status, order.id)}</td>                      <td className="px-6 py-4">{getReceivingBadge(order.receivingStatus)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => { setFeedOrder(order); setShowFeedModal(true); }}
                            className="p-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                            title="Activity Feed">
                            <MessageCircle className="w-4 h-4" />
                          </button>

                          {/* VIEW — laging visible */}
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* EDIT — hidden kung approved AND hindi admin */}
                          {canEditDelete(order.status) && (
                            <button
                              onClick={() => handleEditOrder(order)}
                              className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                              title="Edit Order"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}

                          {/* DELETE — hidden kung approved AND hindi admin */}
                          {canEditDelete(order.status) && (
                            <button
                              onClick={() => handleDeleteOrder(order.id, order.poNumber)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                              title="Delete Order"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                    {showFeedModal && feedOrder && (
                      <ActivityFeedModal_PO
                        order={feedOrder}
                        onClose={() => { setShowFeedModal(false); setFeedOrder(null); }}
                        vendors={vendors}
                        companies={companies}
                      />
                    )}
                    {/* 🔥 EXPANDABLE GR SECTION */}
                    {expandedPO === order.id && (
                      <tr>
                        <td colSpan="9" className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
                          <div className="mb-4 flex items-center gap-3">
                            <Warehouse className="w-5 h-5 text-blue-600" />
                            <h3 className="font-bold text-gray-800">Goods Receipts for {order.poNumber}</h3>
                            <span className="ml-auto bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {grData[order.id]?.length || 0} GRs
                            </span>
                          </div>

                          {grData[order.id]?.length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-xl">
                              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">No Goods Receipts found for this PO</p>
                            </div>
                          ) : (
                            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">GR No</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Item Code</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Description</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Ordered</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Received</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">UOM</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Warehouse</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Batch No</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">GR Date</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {grData[order.id]?.map((gr, idx) => {
                                    const isReverted = gr.remarks?.toUpperCase().includes('REVERTED');
                                    return (
                                      <tr key={idx} className={isReverted ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"}>
                                        <td className="px-4 py-3 text-sm font-semibold">
                                          <div className="flex flex-col gap-1">
                                            <span className={isReverted ? "text-red-500 line-through" : "text-blue-600"}>
                                              {gr.grNo}
                                            </span>
                                            {isReverted && (
                                              <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full w-fit">
                                                ⚠ REVERTED
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.itemCode || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.itemDescription || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.orderedQty ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm font-semibold text-green-600">{gr.receivedQty ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.uom || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.warehouseCode || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{gr.batchNo || '—'}</td>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-sm text-gray-700">{formatDate(gr.grDate)}</span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-16 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No purchase orders found</h3>
                    <p className="text-gray-500">
                      {search || statusFilter !== "all" || receivingFilter !== "all"
                        ? "Try adjusting your search or filters"
                        : "No purchase orders have been created yet"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {paginated.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, filtered.length)} of{" "}
              {filtered.length} orders
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-sm">
                Page {page} of {totalPages || 1}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TOOLTIP */}
      {tooltip.show && (
        <div
          className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none whitespace-pre-line"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {Object.entries(STATUS_MAP).map(([key, value]) => {
          const count = orders.filter((o) => o.status === parseInt(key)).length;
          const Icon = value.icon;
          return (
            <div
              key={key}
              className="bg-white p-4 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 text-${value.color}-600`} />
                <span className={`text-2xl font-bold text-${value.color}-600`}>{count}</span>
              </div>
              <p className="text-sm font-medium text-gray-600">{value.label}</p>
            </div>
          );
        })}
      </div>

      {/* MODALS */}
      {showModal && selectedOrder && (
        <PurchaseOrderModal
          order={selectedOrder}
          onClose={() => {
            setShowModal(false);
            setSelectedOrder(null);
          }}
          companies={companies}
          vendors={vendors}
        />
      )}

      {showEditModal && editingOrder && (
        <PurchaseOrderEditModal
          order={editingOrder}
          onClose={() => {
            setShowEditModal(false);
            setEditingOrder(null);
          }}
          onSave={fetchOrders}
          companies={companies}
          vendors={vendors}
        />
      )}

      <RemarksModal />

    </div>
  );
}